import React from 'react'
import styled from '@emotion/styled'
import { colors, radius, shadow, spacing } from '@mobi/component-library/Foundations'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import type { Ticket } from '@core/Areas/AccountActivity/types'
import { BetInfo } from '@core/Areas/AccountActivity/Components/Ticket/Components/BetInfo'
import { BetInfo as KambiBetInfo } from '@core/Areas/KambiSports/Player/Components/Coupon/Components/BetInfo'
import { KambiHistoryCouponDisplayContext } from '@core/Areas/KambiSports/Player/Components/Coupon/context'
import { TicketDisplayContext } from '@core/Areas/AccountActivity/context'
import { getTicketLayoutType } from '@core/Areas/AccountActivity/Components/Ticket/helpers/legTypes'
import { isKambiSharedCoupon } from '@core/Areas/BetLoop/helpers/typeGuards/isKambiSharedCoupon'
import { trackBetLoopExpandSelection } from '@core/Areas/BetLoop/helpers/analytics'
import { HeightTruncateWrapper } from '@core/Areas/BetLoop/Components/Common/SharedBetCard/Components/BetSelectionDisplay/Components/HeightTruncateWrapper'

export const PendingTicket: React.FC<{
  ticket: Ticket | KambiHistoryCoupon
  isSelected: boolean
  onClick(ticketSequence: Ticket['ticketSequence']): void
}> = ({ ticket, isSelected, onClick }) => {
  const isKambiBet = isKambiSharedCoupon(ticket)
  const ticketNumber = isKambiBet ? ticket.bets[0].betRef : ticket.ticketSequence

  const handleOnChangeCallback = (expanded: boolean) => {
    trackBetLoopExpandSelection(expanded ? 'enable' : 'disable')
  }

  return (
    <WrapperStyled className={isSelected ? 'quick-share__ticket--selected' : ''}>
      <HeightTruncateWrapper
        {...(isKambiBet ? { coupon: ticket } : { ticket })}
        onChangeCallback={handleOnChangeCallback}
      >
        <div
          onClick={() => onClick(ticketNumber)}
          data-testid='QuickShare.Ticket'
          className='quick-share__ticket-content'
        >
          {isKambiBet ? (
            <KambiHistoryCouponDisplayContext.Provider
              value={{ type: 'betloop-quick-share', data: { isSelected } }}
            >
              <KambiBetInfo historyCoupon={ticket as KambiHistoryCoupon} />
            </KambiHistoryCouponDisplayContext.Provider>
          ) : (
            <TicketDisplayContext.Provider
              value={{
                type: 'share',
                data: { shouldHideCost: true, display: 'betloop-quick-share', isSelected },
              }}
            >
              <BetInfo
                ticket={ticket as Ticket}
                status='Pending'
                ticketLayoutType={getTicketLayoutType(ticket as Ticket)}
              />
            </TicketDisplayContext.Provider>
          )}
        </div>
      </HeightTruncateWrapper>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  boxSizing: 'border-box',
  position: 'relative',
  padding: '0.6rem',
  background: colors.white,
  borderRadius: radius.lgx2,
  border: `0.1rem solid ${colors.neutral[200]}`,
  boxShadow: shadow.xs,
  cursor: 'pointer',
  textAlign: 'unset',
  transition: 'all 0.3s ease',
  WebkitTapHighlightColor: 'transparent',
  width: '100%',

  '::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    transition: 'opacity 0.3s ease',
    borderRadius: 'inherit',
    border: `0.2rem solid ${colors.studio[500]}`,
    opacity: 0,
    pointerEvents: 'none',
  },

  '&.quick-share__ticket--selected': {
    background: colors.lavender[50],

    '::before': { opacity: 1 },
  },

  '.quick-share__ticket-content': {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.smx1,
    padding: '0.6rem',
    background: 'white',
    borderRadius: radius.lgx1,
    overflow: 'hidden',

    '> *': {
      // Disable all interaction w/ display ticket
      pointerEvents: 'none',
    },
  },
})
